import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from 'pages/sideBar/SideBar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MonthlyPackagesPage from './MonthlyPakages';

const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="ss-modal-overlay" onClick={onCancel}>
      <div className="ss-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="ss-modal-title">{title}</div>
        <div className="ss-modal-message">{message}</div>
        <div className="ss-modal-actions">
          <button className="ss-modal-btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="ss-modal-btn-confirm" onClick={onConfirm}>Yes, Delete</button>
        </div>
      </div>
    </div>
  );
};

const BASE = 'https://production.washta.com/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

let idCounter = 1000;
const genId = (prefix) => `${prefix}_${idCounter++}`;

// Map API package doc → UI location shape
const mapPkgToLocation = (pkg) => {
  // Collect all unique washCounts across all carTypes
  const washCountSet = new Set();
  pkg.carTypes.forEach((ct) => ct.packages.forEach((p) => washCountSet.add(p.washCount)));

  // Build one tier per washCount, with prices for each carType
  const tiers = Array.from(washCountSet).sort((a, b) => a - b).map((wc) => {
    const prices = {};
    let isPopular = false;
    pkg.carTypes.forEach((ct) => {
      const p = ct.packages.find((p) => p.washCount === wc);
      if (p) {
        prices[ct.carType.toLowerCase()] = p.pricePerMonth;
        if (p.isPopular) isPopular = true;
      }
    });
    return {
      id: `${pkg._id}_${wc}`,
      washCount: wc,
      label: `${wc} Washes / month`,
      active: true,
      showPopular: isPopular,
      prices,
    };
  });

  return {
    id: pkg._id,
    _id: pkg._id,
    shopId: pkg.shopId,
    locationId: pkg.locationId,
    name: pkg.locationDisplayName,
    type: 'residential',
    typeLabel: '🏠 Residential building',
    residentialEnabled: pkg.isActive,
    carTypes: pkg.carTypes.reduce((acc, ct) => { acc[ct.carType.toLowerCase()] = true; return acc; }, {}),
    tiers,
    orders: [],
  };
};

const Packages = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [myLocations, setMyLocations] = useState([]); // seller's shop locations
  const [expandedServices, setExpandedServices] = useState({});
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const openConfirm = (title, message, onConfirm) => setConfirmModal({ isOpen: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({ shopId: '', locationId: '', locationDisplayName: '' });
  const [addStep, setAddStep] = useState(1);
  const [newCarTypes, setNewCarTypes] = useState({ Sedan: true, SUV: false, Van: false });
  const [newTiers, setNewTiers] = useState([{ id: 1, washCount: '', prices: { Sedan: '', SUV: '', Van: '' }, isPopular: false }]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPackages();
    fetchMyLocations();
  }, []);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${BASE}/residential/seller/packages`, { headers: authHeaders() });
      setLocations((res.data.data || []).map(mapPkgToLocation));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLocations = async () => {
    try {
      const res = await axios.get(`${BASE}/residential/seller/my-locations`, { headers: authHeaders() });
      const data = res.data.data || res.data || [];
      setMyLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('fetchMyLocations error:', err?.response?.data || err.message);
    }
  };

  // ---------- Location handlers ----------
  const toggleResidential = async (locId) => {
    const loc = locations.find((l) => l.id === locId);
    if (!loc?._id) {
      // Locally toggle only (new location not yet saved)
      setLocations((prev) =>
        prev.map((l) => l.id === locId ? { ...l, residentialEnabled: !l.residentialEnabled } : l)
      );
      return;
    }
    try {
      await axios.patch(`${BASE}/residential/seller/packages/${loc._id}/toggle`, {}, { headers: authHeaders() });
      setLocations((prev) =>
        prev.map((l) => l.id === locId ? { ...l, residentialEnabled: !l.residentialEnabled } : l)
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to toggle. Please try again.');
    }
  };

  const toggleServicesExpand = (locId) => {
    setExpandedServices((prev) => ({ ...prev, [locId]: !prev[locId] }));
  };

  const deleteLocation = (locId) => {
    const loc = locations.find((l) => l.id === locId);
    openConfirm(
      'Delete Location',
      `Are you sure you want to delete "${loc?.name}"? This cannot be undone.`,
      async () => {
        closeConfirm();
        try {
          if (loc?._id) await axios.delete(`${BASE}/residential/seller/packages/${loc._id}`, { headers: authHeaders() });
          setLocations((prev) => prev.filter((l) => l.id !== locId));
          toast.success('Package deleted successfully');
        } catch (err) {
          toast.error(err?.response?.data?.error || 'Failed to delete location');
        }
      }
    );
  };

  const openAddLocation = () => {
    setNewLocation({ shopId: '', locationId: '', locationDisplayName: '' });
    setNewCarTypes({ Sedan: true, SUV: false, Van: false });
    setNewTiers([{ id: Date.now(), washCount: '', prices: { Sedan: '', SUV: '', Van: '' }, isPopular: false }]);
    setShowAddLocation(true);
  };

  const submitAddLocation = () => {
    if (!newLocation.shopId || !newLocation.locationId) { toast.error('Please select a location'); return; }
    const tempId = `new_${Date.now()}`;
    const localLoc = {
      id: tempId,
      _id: null,
      shopId: newLocation.shopId,
      locationId: newLocation.locationId,
      name: newLocation.locationDisplayName,
      type: 'residential',
      typeLabel: '🏠 Residential building',
      residentialEnabled: true,
      carTypes: { sedan: true, suv: false, van: false },
      tiers: [{ id: genId('tier'), washCount: 1, label: '1 Washes / month', active: true, showPopular: false, prices: { sedan: '' } }],
      orders: [],
      isNew: true,
    };
    setLocations((prev) => [...prev, localLoc]);
    setExpandedServices((prev) => ({ ...prev, [tempId]: true }));
    setShowAddLocation(false);
  };

  // ---------- Car type handler (FIXED — was missing onClick) ----------
  const toggleCarType = (locId, ct) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId
          ? { ...loc, carTypes: { ...(loc.carTypes || {}), [ct]: !loc.carTypes?.[ct] } }
          : loc
      )
    );
  };

  // ---------- Tier handlers ----------
  const toggleTierActive = (locId, tierId) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId
          ? { ...loc, tiers: loc.tiers.map((t) => (t.id === tierId ? { ...t, active: !t.active } : t)) }
          : loc
      )
    );
  };

  // Toggling "showPopular" now correctly shows/hides the badge on THIS tier,
  // AND keeps the On/Off active-toggle visible at the same time.
  const toggleTierPopular = (locId, tierId) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId
          ? { ...loc, tiers: loc.tiers.map((t) => (t.id === tierId ? { ...t, showPopular: !t.showPopular } : t)) }
          : loc
      )
    );
  };

  const updateTierField = (locId, tierId, field, carType, value) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId
          ? {
              ...loc,
              tiers: loc.tiers.map((t) =>
                t.id === tierId
                  ? field === 'label'
                    ? { ...t, label: value }
                    : field === 'washCount'
                    ? { ...t, washCount: value, label: value ? `${value} Washes / month` : '' }
                    : { ...t, prices: { ...t.prices, [carType]: value } }
                  : t
              ),
            }
          : loc
      )
    );
  };

  // ---------- Add / Delete tier (FIXED — functions + buttons were missing) ----------
  const addTier = (locId) => {
    const loc = locations.find((l) => l.id === locId);
    const enabledCTs = Object.keys(loc?.carTypes || {}).filter((k) => loc.carTypes[k]);
    const prices = enabledCTs.reduce((acc, ct) => { acc[ct] = ''; return acc; }, {});
    const newTier = {
      id: genId('tier'),
      washCount: '',
      label: '',
      active: true,
      showPopular: false,
      prices,
    };
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId ? { ...loc, tiers: [...(loc.tiers || []), newTier] } : loc
      )
    );
  };

  const deleteTier = (locId, tierId) => {
    openConfirm(
      'Delete Tier',
      'Are you sure you want to delete this package tier?',
      () => {
        closeConfirm();
        setLocations((prev) =>
          prev.map((loc) =>
            loc.id === locId ? { ...loc, tiers: loc.tiers.filter((t) => t.id !== tierId) } : loc
          )
        );
      }
    );
  };

  const perWashLabel = (tier, enabledCarTypes) => {
    const parts = (enabledCarTypes || ['sedan', 'suv'])
      .filter((ct) => tier.prices[ct])
      .map((ct) => {
        const perWash = (Number(tier.prices[ct]) / Number(tier.washCount)).toFixed(1);
        return `${ct.charAt(0).toUpperCase() + ct.slice(1)} AED ${perWash}/wash`;
      });
    return parts.join(' · ') || '';
  };

  // ---------- Services (Edit services modal) handlers ----------
  const addService = (locId) => {
    const newService = { id: genId('svc'), name: 'New service', price: 0 };
    setLocations((prev) =>
      prev.map((loc) => (loc.id === locId ? { ...loc, services: [...(loc.services || []), newService] } : loc))
    );
  };

  const updateService = (locId, svcId, field, value) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId
          ? { ...loc, services: loc.services.map((s) => (s.id === svcId ? { ...s, [field]: value } : s)) }
          : loc
      )
    );
  };

  const deleteService = (locId, svcId) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId ? { ...loc, services: loc.services.filter((s) => s.id !== svcId) } : loc
      )
    );
  };

  const cancelLocation = (locId) => {
    const loc = locations.find((l) => l.id === locId);
    if (loc?.isNew) {
      setLocations((prev) => prev.filter((l) => l.id !== locId));
    } else {
      setExpandedServices((prev) => ({ ...prev, [locId]: false }));
      fetchPackages();
    }
  };

  const saveLocation = async (locId) => {
    const loc = locations.find((l) => l.id === locId);
    if (!loc) return;
    try {
      setSaving(true);
      const enabledCTs = Object.keys(loc.carTypes || {}).filter((k) => loc.carTypes[k]);
      if (!enabledCTs.length || !loc.tiers?.length) {
        toast.error('Add at least one car type and tier before saving');
        return;
      }
      const carTypes = enabledCTs.map((ct) => ({
        carType: ct.charAt(0).toUpperCase() + ct.slice(1),
        packages: (loc.tiers || []).filter((t) => t.prices[ct]).map((t) => ({
          washCount: Number(t.washCount),
          pricePerMonth: Number(t.prices[ct]),
          isPopular: t.showPopular || false,
        })),
      })).filter((ct) => ct.packages.length > 0);
      if (!carTypes.length) { toast.error('Add pricing for at least one tier'); return; }
      await axios.post(
        `${BASE}/residential/seller/packages`,
        { shopId: loc.shopId, locationId: loc.locationId, locationDisplayName: loc.name, carTypes },
        { headers: authHeaders() }
      );
      toast.success('Changes saved successfully');
      fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mainDiv-all-sideBar">
        <div className="div-sidebar"><Sidebar /></div>
        {/* <div className="div-Dashbaord" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 13, color: '#888' }}>Loading packages...</div>
        </div> */}
      </div>
    );
  }

  return (
    <div className="mainDiv-all-sideBar">
      <div className="div-sidebar">
        <Sidebar />
      </div>
      <div className="div-Dashbaord packages-page" style={{ padding: 0 }}>
        <div className="dashboard">
            <MonthlyPackagesPage />
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />

      {/* ---------- ADD LOCATION MODAL ---------- */}
      {showAddLocation && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999}} onClick={() => setShowAddLocation(false)}>
          <div style={{background:'#fff',borderRadius:14,padding:'24px 26px',width:460,maxWidth:'94vw',boxShadow:'0 8px 40px rgba(0,0,0,.25)'}} onClick={(e) => e.stopPropagation()}>

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <div style={{fontSize:14,fontWeight:700,color:'#111'}}>Add Residential Location</div>
              <span style={{fontSize:18,color:'#bbb',cursor:'pointer',lineHeight:1}} onClick={() => setShowAddLocation(false)}>✕</span>
            </div>

            <div style={{fontSize:9,fontWeight:700,color:'#aaa',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Select Location</div>
            {myLocations.length === 0 ? (
              <div style={{fontSize:12,color:'#888',padding:12,background:'#f7f7f9',borderRadius:8,textAlign:'center',marginBottom:20}}>No locations found. Add a shop with locations first.</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:20}}>
                {myLocations.map((l) => {
                  const sel = newLocation.locationId === String(l.locationId);
                  return (
                    <div key={String(l.locationId)}
                      onClick={() => setNewLocation({shopId:l.shopId,locationId:String(l.locationId),locationDisplayName:l.locationDisplayName||l.city||''})}
                      style={{border:`1.5px solid ${sel?'#6B5FE4':'#e8e8ec'}`,borderRadius:10,padding:'10px 14px',cursor:'pointer',background:sel?'#f0eeff':'#fff',display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:13,height:13,borderRadius:'50%',border:`2px solid ${sel?'#6B5FE4':'#ccc'}`,background:sel?'#6B5FE4':'#fff',flexShrink:0}} />
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:'#111'}}>{l.locationDisplayName||l.city}</div>
                        <div style={{fontSize:10,color:'#888'}}>{l.shopName}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{fontSize:10,color:'#aaa',marginBottom:20,background:'#f7f7f9',borderRadius:8,padding:'8px 12px'}}>
              💡 After adding, enable the toggle and configure car types &amp; package tiers directly on the location card.
            </div>

            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <div style={{fontSize:11,fontWeight:600,padding:'7px 16px',border:'1px solid #ddd',color:'#666',borderRadius:8,cursor:'pointer'}} onClick={() => setShowAddLocation(false)}>Cancel</div>
              <div style={{fontSize:11,fontWeight:700,padding:'8px 20px',background:newLocation.locationId?'#6B5FE4':'#ccc',color:'#fff',borderRadius:8,cursor:newLocation.locationId?'pointer':'not-allowed',opacity:saving?0.7:1}}
                onClick={newLocation.locationId ? submitAddLocation : undefined}>
                {saving?'Adding...':'Add Location'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Scoped CSS ---------- */}
      <style>{`

        .ss-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:99999;}
        .ss-modal-box{background:#fff;border-radius:12px;padding:28px 28px 22px;width:360px;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,.18);}
        .ss-modal-title{font-size:16px;font-weight:600;color:#1a1a1a;margin-bottom:10px;}
        .ss-modal-message{font-size:14px;color:#555;line-height:1.6;margin-bottom:22px;}
        .ss-modal-actions{display:flex;justify-content:flex-end;gap:10px;}
        .ss-modal-btn-cancel{padding:8px 18px;border-radius:7px;border:1px solid #ddd;background:#f5f5f5;color:#444;font-size:13px;cursor:pointer;}
        .ss-modal-btn-cancel:hover{background:#ebebeb;}
        .ss-modal-btn-confirm{padding:8px 18px;border-radius:7px;border:none;background:#e53935;color:#fff;font-size:13px;font-weight:500;cursor:pointer;}
        .ss-modal-btn-confirm:hover{background:#c62828;}
        .packages-page .admin-shell{background:#fff;box-shadow:0 4px 24px rgba(0,0,0,.08);}
        .packages-page .view-block{padding:20px 22px;}
        .packages-page .view-title{font-size:15px;font-weight:700;color:#111;margin-bottom:4px;}
        .packages-page .view-sub{font-size:11px;color:#888;margin-bottom:18px;max-width:640px;}

        .packages-page .btn-primary{font-size:11px;font-weight:600;padding:7px 14px;background:#6B5FE4;color:#fff;border-radius:8px;cursor:pointer;white-space:nowrap;}
        .packages-page .btn-ghost{font-size:11px;font-weight:600;padding:7px 14px;border:1px solid #ddd;color:#666;border-radius:8px;cursor:pointer;}
        .packages-page .btn-save{font-size:11px;font-weight:700;padding:8px 18px;background:#6B5FE4;color:#fff;border-radius:8px;cursor:pointer;}

        .packages-page .field-label{font-size:9px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.07em;margin-bottom:5px;}
        .packages-page .edit-input{width:100%;font-size:12px;color:#111;border:1px solid #ddd;border-radius:7px;padding:6px 10px;background:#fff;font-family:inherit;}
        .packages-page .edit-input:focus{outline:none;border-color:#6B5FE4;}
        .packages-page .select-input{width:100%;font-size:12px;color:#111;border:1px solid #ddd;border-radius:7px;padding:6px 10px;background:#fff;font-family:inherit;}

        .packages-page .info-banner{background:#f0eeff;border:1px solid #d8d2ff;border-radius:10px;padding:10px 14px;margin-bottom:18px;display:flex;align-items:flex-start;gap:10px;}
        .packages-page .info-icon{font-size:14px;flex-shrink:0;margin-top:1px;}
        .packages-page .info-text{font-size:11px;color:#4a3db8;line-height:1.5;}

        .packages-page .success-banner{background:#e0f2ea;border:1px solid #6ee7b7;border-radius:10px;padding:10px 14px;margin-bottom:18px;display:flex;align-items:flex-start;gap:10px;}
        .packages-page .success-text{font-size:11px;color:#1a6641;line-height:1.5;}

        .packages-page .loc-card{border:1px solid #e8e8ec;border-radius:12px;margin-bottom:14px;overflow:hidden;}
        .packages-page .loc-header{display:flex;align-items:center;gap:12px;padding:10px 14px;background:#f7f7f9;border-bottom:1px solid #eee;}
        .packages-page .loc-name-block{flex:1;}
        .packages-page .loc-name{font-size:13px;font-weight:700;color:#111;}
        .packages-page .loc-type{font-size:10px;color:#888;margin-top:1px;}
        .packages-page .loc-actions{display:flex;gap:12px;align-items:center;}
        .packages-page .action-link{font-size:10px;color:#6B5FE4;cursor:pointer;white-space:nowrap;font-weight:600;}
        .packages-page .loc-body{padding:14px;display:flex;flex-direction:column;gap:14px;}

        .packages-page .res-toggle-row{display:flex;align-items:center;justify-content:space-between;background:#faf9ff;border:1px solid #d8d2ff;border-radius:10px;padding:10px 14px;}
        .packages-page .res-toggle-label{font-size:12px;font-weight:600;color:#111;}
        .packages-page .res-toggle-sub{font-size:10px;color:#888;margin-top:2px;}
        .packages-page .mini-toggle{width:36px;height:20px;border-radius:10px;background:#ddd;position:relative;flex-shrink:0;cursor:pointer;}
        .packages-page .mini-toggle.on{background:#6B5FE4;}
        .packages-page .toggle-knob{position:absolute;top:3px;left:3px;width:14px;height:14px;background:#fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,.2);}
        .packages-page .mini-toggle.on .toggle-knob{left:19px;}

        .packages-page .res-panel{background:#faf9ff;border:1px solid #d8d2ff;border-radius:10px;padding:14px;margin-top:2px;}
        .packages-page .res-panel-title{font-size:11px;font-weight:700;color:#4a3db8;margin-bottom:12px;display:flex;align-items:center;gap:6px;}

        .packages-page .pkg-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:12px;}
        .packages-page .pkg-tier-card{border:1px solid #e8e8ec;border-radius:10px;overflow:hidden;background:#fff;}
        .packages-page .pkg-tier-card.featured{border-color:#6B5FE4;box-shadow:0 0 0 2px #f0eeff;}
        .packages-page .pkg-tier-head{background:#f7f7f9;border-bottom:1px solid #eee;padding:8px 12px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:6px;}
        .packages-page .pkg-tier-card.featured .pkg-tier-head{background:#f0eeff;}
        .packages-page .pkg-tier-label{font-size:11px;font-weight:700;color:#111;}
        .packages-page .pkg-featured-badge{font-size:8px;font-weight:700;background:#6B5FE4;color:#fff;padding:2px 7px;border-radius:100px;white-space:nowrap;}
        .packages-page .pkg-tier-body{padding:12px;display:flex;flex-direction:column;gap:10px;}

        .packages-page .price-matrix{display:flex;flex-direction:column;gap:6px;}
        .packages-page .price-row{display:flex;align-items:center;gap:8px;}
        .packages-page .price-car{font-size:10px;color:#888;width:56px;flex-shrink:0;}
        .packages-page .price-wrap{display:flex;align-items:center;border:1px solid #ddd;border-radius:7px;overflow:hidden;background:#fff;flex:1;}
        .packages-page .price-cur{font-size:10px;color:#888;padding:5px 7px;background:#f7f7f9;border-right:1px solid #ddd;}
        .packages-page .price-input{flex:1;font-size:12px;font-weight:700;color:#111;border:none;padding:5px 7px;font-family:inherit;width:100%;}
        .packages-page .price-input:focus{outline:none;}
        .packages-page .per-wash{font-size:9px;color:#6B5FE4;font-weight:600;margin-top:3px;}

        .packages-page .toggle-row{display:flex;align-items:center;gap:7px;}
        .packages-page .popular-row{display:flex;align-items:center;gap:6px;font-size:10px;color:#888;}

        .packages-page .save-bar{display:flex;align-items:center;gap:10px;justify-content:flex-end;padding:12px 22px;background:#f7f7f9;border-top:1px solid #eee;}
        .packages-page .save-hint{font-size:10px;color:#bbb;margin-right:auto;}

        .packages-page .plate-badge{display:inline-block;background:#111;color:#fff;font-size:10px;font-weight:800;padding:3px 9px;border-radius:5px;letter-spacing:.05em;font-family:monospace;}
        .packages-page .action-completed{font-size:10px;font-weight:700;padding:5px 14px;background:#d1fae5;color:#1a6641;border-radius:100px;white-space:nowrap;border:1px solid #6ee7b7;}
        .packages-page .count-badge{font-size:10px;font-weight:700;padding:3px 9px;border-radius:100px;}
        .packages-page .count-pending{background:#FEF3C7;color:#92400E;}

        .packages-page .wash-table{width:100%;border-collapse:collapse;}
        .packages-page .wash-table th{font-size:9px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.06em;padding:8px 10px;text-align:left;border-bottom:1px solid #eee;background:#fafafa;}
        .packages-page .wash-table td{font-size:11px;color:#111;padding:9px 10px;border-bottom:1px solid #f5f5f5;vertical-align:middle;}
        .packages-page .wash-table tr:last-child td{border-bottom:none;}

        /* Modal */
        .packages-page .modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:99999;}
        .packages-page .modal-box{background:#fff;border-radius:14px;padding:22px 24px;width:400px;max-width:90vw;max-height:80vh;overflow-y:auto;box-shadow:0 8px 40px rgba(0,0,0,.25);}
        .packages-page .modal-wide{width:600px;}
        .packages-page .modal-title{font-size:14px;font-weight:700;color:#111;margin-bottom:16px;}
      `}</style>
    </div>
  );
};

export default Packages;
